// The MODEL RUNNER: try a model on one record.
//
// A model's training tab answers "can I trust it" (metrics, lineage,
// importances). The runner answers the next question: "what do I feed it, and
// what comes back?" — so it leads with the INPUT CONTRACT (every feature, its
// type, a real example value pulled from the governed data), runs a single
// prediction through the same allowlisted engine proxy the training tab uses,
// and reports the engine's own timing next to the round trip, because "how
// fast is scoring" decides whether a workflow can call it inline.
//
// Two mounts, one component: <ModelRunner> is the DEFAULT TAB of the model
// detail (Models.jsx), and the models/:modelName/run route below wraps the
// same component as a standalone page so the runner stays deep-linkable.
//
// The DEPENDANTS panel lives in the runner: before promoting or removing a
// model you want "where is this used" in one place — pipeline nodes
// (ml_predict strategies), workflow steps calling cortex.ml.predict, and how
// many predictions it has landed in the governed output table. Every
// dependant is a link that opens in a NEW TAB. Apps have no first-class model
// binding (there is no model_app), so that row explains itself instead of
// pretending.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Loader2, Play, AlertTriangle, ArrowLeft, Hash, Type, Timer,
  GitBranch, Workflow, Database, LayoutGrid, ExternalLink, RefreshCw,
  ChevronRight, Zap,
} from "lucide-react";
import { listModels, getModel, predictWith } from "../services/models";
import { wfEngine } from "../services/workflows";
import { listStrategies, listNodes } from "../services/pipeline";
import { createPageUrl } from "@/utils";
import { useProject } from "@/contexts/ProjectContext";

const btnPrimary = "inline-flex items-center gap-1.5 rounded-md bg-nav-active text-white text-sm px-3 py-1.5 border-0 cursor-pointer disabled:opacity-50";
const btnGhost = "inline-flex items-center gap-1.5 rounded-md text-sm px-3 py-1.5 border border-card-border bg-transparent text-page-text cursor-pointer hover:bg-card-border/30 disabled:opacity-50";
const inputCls = "w-full rounded-md border border-card-border bg-page-bg text-page-text text-sm px-3 py-1.5 outline-none focus:border-nav-active";
const labelCls = "block text-xs uppercase tracking-wide text-page-text/60 mb-1";

const asObj = (v) => {
  if (v && typeof v === "object") return v;
  try { return JSON.parse(v); } catch { return null; }
};

// One place decides how a feature value leaves the form: numbers go as numbers,
// blanks go as null (the engine imputes), everything else as the typed string.
function coerce(value, isNumeric) {
  if (value === "" || value === null || value === undefined) return null;
  if (!isNumeric) return String(value);
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

const fmtMs = (v) => (v === null || v === undefined ? "—" : `${Math.round(Number(v))} ms`);
const fmtCell = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return Number.isInteger(v) ? String(v) : v.toFixed(3);
  return String(v);
};

// ── the runner itself — embeddable (default tab) and routable (page below) ──
export function ModelRunner({ model, versions }) {
  const version = (versions || []).find((v) => v.id === model.active_version_id) || null;

  const [examples, setExamples] = useState({ loading: true, rows: [] });
  const [values, setValues] = useState({});
  const [exampleIdx, setExampleIdx] = useState(0);
  const [threshold, setThreshold] = useState("0.5");
  const [running, setRunning] = useState(false);
  const [runs, setRuns] = useState([]);          // newest first: {n, score, label, cached, load, scoreMs, total, roundtrip, error?}
  const [deps, setDeps] = useState({ loading: true, strategies: [], workflows: [], predictions: null });

  const spec = useMemo(() => asObj(version?.feature_spec) || {}, [version]);
  const allCols = useMemo(() => [
    ...(spec.numeric || []).map((c) => ({ name: c, numeric: true })),
    ...(spec.categorical || []).map((c) => ({ name: c, numeric: false })),
  ], [spec]);

  // ── real example rows, from the version's own training query ─────────────
  // The training SELECT is the input contract's source of truth, so example
  // values come from running it (SELECT-only, engine-enforced) rather than
  // from anything invented here. If the preview path is unavailable the form
  // still works — examples are a convenience, not a dependency.
  useEffect(() => {
    const sql = version?.training_sql;
    if (!sql) { setExamples({ loading: false, rows: [] }); return; }
    let gone = false;
    (async () => {
      try {
        const wrapped = `select * from (${String(sql).replace(/;\s*$/, "")}) _example limit 5`;
        const r = await wfEngine("query_data", { sql: wrapped });
        if (!gone) setExamples({ loading: false, rows: r?.rows || [] });
      } catch {
        if (!gone) setExamples({ loading: false, rows: [] });
      }
    })();
    return () => { gone = true; };
  }, [version]);

  // Prefill the form from the first example as soon as both have arrived.
  useEffect(() => {
    if (!examples.rows.length || !allCols.length) return;
    setValues((prev) => (Object.keys(prev).length ? prev : rowToValues(examples.rows[0], allCols)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examples.rows, allCols.length]);

  function rowToValues(row, cols) {
    const v = {};
    for (const c of cols) v[c.name] = row?.[c.name] === null || row?.[c.name] === undefined ? "" : String(row[c.name]);
    return v;
  }

  function loadExample() {
    if (!examples.rows.length) return;
    const next = (exampleIdx + 1) % examples.rows.length;
    setExampleIdx(next);
    setValues(rowToValues(examples.rows[next], allCols));
  }

  // ── dependants: where this model is used ─────────────────────────────────
  const loadDeps = useCallback(async () => {
    setDeps((d) => ({ ...d, loading: true }));
    const artifactKeys = new Set();
    for (const v of versions || []) if (v.artifact_key) artifactKeys.add(v.artifact_key);

    // A pipeline transform is an ml_predict node whose params name this model.
    // Two storage shapes carry it: the NODE-NATIVE graph (`node` rows, the
    // canvas's own model) and the legacy `strategy` projection — scan both so
    // the dependant shows up regardless of which the project uses.
    const refsModel = (params) => {
      const p = asObj(params) || {};
      return p.model_name === model.name
        || (p.artifact_key && (artifactKeys.has(p.artifact_key) || String(p.artifact_key).includes(`/${model.name}/`)));
    };
    const [strategies, workflows, predictions] = await Promise.all([
      Promise.all([
        listNodes().then((r) => (r?.nodes || []).filter((n) => n.kind === "ml_predict" && refsModel(n.params))
          .map((n) => ({ id: `node:${n.id}`, label: `ml_predict node #${n.id}` }))).catch(() => []),
        listStrategies().then((r) => (r?.strategies || []).filter((s) => s.kind === "ml_predict" && refsModel(s.params))
          .map((s) => ({ id: `strategy:${s.id}`, label: `ml_predict node #${s.id}` }))).catch(() => []),
      ]).then(([nodes, strats]) => [...nodes, ...strats]),
      // workflow steps that call the scoring endpoint; a step is tied to THIS
      // model when its config mentions the model by name or artifact
      (async () => {
        try {
          const defs = (await wfEngine("list_definitions", {}))?.definitions || [];
          const out = [];
          for (const d of defs.slice(0, 25)) {
            try {
              const full = (await wfEngine("get_definition", { id: d.id }))?.definition;
              const nodes = full?.graph?.nodes || [];
              const scoring = nodes.filter((n) => /cortex\.ml\.predict/.test(String(n.endpoint || "")));
              if (!scoring.length) continue;
              const blob = JSON.stringify(scoring);
              const mine = blob.includes(model.name) || [...artifactKeys].some((k) => blob.includes(k));
              out.push({ id: d.id, name: d.name || `process ${d.id}`, steps: scoring.length, mine });
            } catch { /* skip unreadable definitions */ }
          }
          return out;
        } catch { return []; }
      })(),
      // rows this model has landed in the governed output table
      (async () => {
        try {
          const safe = model.name.replace(/'/g, "''");
          const r = await wfEngine("query_data", {
            sql: `select count(*) as n, max(model_version) as latest_version from ml_prediction where model_name = '${safe}'`,
          });
          const row = r?.rows?.[0];
          return row ? { n: Number(row.n) || 0, latest: row.latest_version } : null;
        } catch { return null; }
      })(),
    ]);
    setDeps({ loading: false, strategies, workflows, predictions });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model.id, model.name]);

  useEffect(() => { loadDeps(); }, [loadDeps]);

  // ── run one prediction ────────────────────────────────────────────────────
  async function run(e) {
    e?.preventDefault?.();
    if (!version?.artifact_key || running) return;
    setRunning(true);
    const record = {};
    for (const c of allCols) record[c.name] = coerce(values[c.name], c.numeric);
    const th = Number(threshold) || 0.5;
    const t0 = performance.now();
    try {
      const r = await predictWith({
        artifact_key: version.artifact_key,
        artifact_bucket: version.artifact_bucket,
        // a `fork:<name>` version is scored by this project's own service
        algorithm: version.algorithm || undefined,
        records: [record],
        threshold: th,
      });
      const roundtrip = performance.now() - t0;
      const p = r?.predictions?.[0] || {};
      const t = r?.timing_ms || {};
      setRuns((prev) => [{
        n: prev.length + 1,
        score: p.score,
        label: p.label !== undefined ? p.label : (p.score >= th ? 1 : 0),
        threshold: th,
        cached: !!r?.cached,
        load: t.load, scoreMs: t.score, total: t.total,
        roundtrip,
      }, ...prev].slice(0, 20));
    } catch (err) {
      setRuns((prev) => [{
        n: prev.length + 1,
        error: String(err?.message || err).split("\n")[0].slice(0, 200),
        roundtrip: performance.now() - t0,
      }, ...prev].slice(0, 20));
    } finally {
      setRunning(false);
    }
  }

  const latest = runs[0] || null;
  const exampleRow = examples.rows[exampleIdx] || examples.rows[0] || null;
  const notTrained = !version || !version.artifact_key;

  return (
    <div className="grid gap-5" data-tour="model-run-page">
      {notTrained && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm px-3 py-2 flex items-center gap-2">
          <AlertTriangle size={15} className="shrink-0" />
          This model has no active trained version yet — train one on the Versions &amp; training tab first.
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-2 items-start">
        {/* ── INPUT ── */}
        <section className="rounded-lg border border-card-border bg-card-bg p-4 grid gap-4" data-tour="model-run-input">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-page-text m-0">Input — one record</h2>
            <button type="button" className={btnGhost + " text-xs"} onClick={loadExample}
                    disabled={!examples.rows.length} data-tour="model-run-example"
                    title={examples.rows.length ? "Fill the form from a real governed row" : "No example rows available"}>
              <RefreshCw size={13} />
              {examples.loading ? "Loading examples…" : examples.rows.length ? `Load example (${exampleIdx + 1}/${examples.rows.length})` : "No examples"}
            </button>
          </div>

          {/* the input contract: every feature, its type, a real example */}
          <div className="rounded-md border border-card-border overflow-hidden">
            <table className="w-full text-xs border-collapse" data-tour="model-run-contract">
              <thead>
                <tr className="bg-card-border/30 text-page-text/60">
                  <th className="text-left font-medium px-2.5 py-1.5">Expected input</th>
                  <th className="text-left font-medium px-2.5 py-1.5 w-20">Type</th>
                  <th className="text-left font-medium px-2.5 py-1.5">Example</th>
                  <th className="text-left font-medium px-2.5 py-1.5 w-36">Your value</th>
                </tr>
              </thead>
              <tbody>
                {allCols.map((c) => (
                  <tr key={c.name} className="border-t border-card-border">
                    <td className="px-2.5 py-1.5">
                      <code className="font-mono text-page-text">{c.name}</code>
                    </td>
                    <td className="px-2.5 py-1.5">
                      <span className="inline-flex items-center gap-1 text-page-text/70">
                        {c.numeric ? <Hash size={11} /> : <Type size={11} />}
                        {c.numeric ? "number" : "category"}
                      </span>
                    </td>
                    <td className="px-2.5 py-1.5 text-page-text/60 font-mono">
                      {examples.loading ? "…" : fmtCell(exampleRow?.[c.name])}
                    </td>
                    <td className="px-2.5 py-1">
                      <input
                        className={inputCls + " text-xs py-1"}
                        type={c.numeric ? "number" : "text"}
                        step={c.numeric ? "any" : undefined}
                        list={!c.numeric ? `opts-${c.name}` : undefined}
                        value={values[c.name] ?? ""}
                        onChange={(e) => setValues((v) => ({ ...v, [c.name]: e.target.value }))}
                        data-run-field={c.name}
                      />
                      {!c.numeric && (
                        <datalist id={`opts-${c.name}`}>
                          {[...new Set(examples.rows.map((r) => r?.[c.name]).filter((x) => x !== null && x !== undefined).map(String))]
                            .map((x) => <option key={x} value={x} />)}
                        </datalist>
                      )}
                    </td>
                  </tr>
                ))}
                {!allCols.length && (
                  <tr><td colSpan={4} className="px-2.5 py-3 text-page-muted">
                    No feature specification on the active version.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-end gap-3 flex-wrap">
            <div className="w-28">
              <label className={labelCls} htmlFor="run-threshold">Threshold</label>
              <input id="run-threshold" className={inputCls} type="number" min="0" max="1" step="0.05"
                     value={threshold} onChange={(e) => setThreshold(e.target.value)} />
            </div>
            <button type="button" className={btnPrimary} onClick={run}
                    disabled={notTrained || running || !allCols.length} data-tour="model-run-submit">
              {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
              {running ? "Scoring…" : "Run model"}
            </button>
            <div className="text-[11px] text-page-text/50">
              {model.task === "regression"
                ? <>Output: the predicted value of <code className="font-mono">{model.target_column || "the target"}</code> (a number; no label).</>
                : <>Output: a score 0–1 — the probability that <code className="font-mono">{model.target_column || "the target"}</code> is 1 — and a label at the threshold.</>}
            </div>
          </div>
        </section>

        {/* ── OUTPUT ── */}
        <section className="rounded-lg border border-card-border bg-card-bg p-4 grid gap-4" data-tour="model-run-output">
          <h2 className="text-sm font-semibold text-page-text m-0">Output</h2>

          {!latest && (
            <div className="text-sm text-page-muted py-6 text-center">
              Run the model to see its prediction here.
            </div>
          )}

          {latest && latest.error && (
            <div className="rounded-md border border-red-500/40 bg-red-500/10 text-red-500 text-sm px-3 py-2"
                 data-tour="model-run-error">
              {latest.error}
            </div>
          )}

          {latest && !latest.error && (
            <div className="flex items-center gap-5 flex-wrap" data-tour="model-run-result">
              <div>
                <div className={labelCls}>{model.task === "regression" ? "Predicted value" : "Score"}</div>
                <div className="text-3xl font-semibold tabular-nums text-page-text">
                  {Number(latest.score).toFixed(3)}
                </div>
              </div>
              {model.task !== "regression" && <div>
                <div className={labelCls}>Label @ {latest.threshold}</div>
                <span className={
                  "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-medium " +
                  (Number(latest.label) === 1
                    ? "bg-red-500/15 text-red-500"
                    : "bg-emerald-500/15 text-emerald-600")
                }>
                  {Number(latest.label) === 1 ? <AlertTriangle size={14} /> : <Zap size={14} />}
                  {Number(latest.label) === 1 ? `${model.target_column || "positive"} = 1 (flagged)` : `${model.target_column || "positive"} = 0`}
                </span>
              </div>}
              <div>
                <div className={labelCls}>Engine</div>
                <span className="text-sm text-page-text/80 inline-flex items-center gap-1.5">
                  <Timer size={13} className="opacity-60" />
                  {fmtMs(latest.total)} {latest.cached ? "(warm)" : "(cold start)"}
                </span>
              </div>
            </div>
          )}

          {/* performance metrics for the session's test runs */}
          {runs.length > 0 && (
            <div>
              <div className={labelCls}>Test-run performance</div>
              <div className="rounded-md border border-card-border overflow-x-auto">
                <table className="w-full text-xs border-collapse whitespace-nowrap" data-tour="model-run-timing">
                  <thead>
                    <tr className="bg-card-border/30 text-page-text/60">
                      <th className="text-left font-medium px-2.5 py-1.5">#</th>
                      <th className="text-left font-medium px-2.5 py-1.5">Score</th>
                      <th className="text-left font-medium px-2.5 py-1.5">Cache</th>
                      <th className="text-right font-medium px-2.5 py-1.5">Artifact load</th>
                      <th className="text-right font-medium px-2.5 py-1.5">Score time</th>
                      <th className="text-right font-medium px-2.5 py-1.5">Engine total</th>
                      <th className="text-right font-medium px-2.5 py-1.5">Round trip</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.map((r) => (
                      <tr key={r.n} className="border-t border-card-border tabular-nums">
                        <td className="px-2.5 py-1.5 text-page-text/60">{r.n}</td>
                        <td className="px-2.5 py-1.5 font-mono">{r.error ? "error" : Number(r.score).toFixed(3)}</td>
                        <td className="px-2.5 py-1.5">{r.error ? "—" : r.cached ? "warm" : "cold"}</td>
                        <td className="px-2.5 py-1.5 text-right">{fmtMs(r.load)}</td>
                        <td className="px-2.5 py-1.5 text-right">{fmtMs(r.scoreMs)}</td>
                        <td className="px-2.5 py-1.5 text-right">{fmtMs(r.total)}</td>
                        <td className="px-2.5 py-1.5 text-right">{fmtMs(r.roundtrip)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-[11px] text-page-text/50 mt-1">
                Engine total = artifact load + scoring inside the model service; round trip adds the
                proxy and network. The first call after a promotion or restart pays the cold artifact load.
              </div>
            </div>
          )}
        </section>
      </div>

      {/* ── DEPENDANTS ── */}
      <section className="rounded-lg border border-card-border bg-card-bg p-4 grid gap-3" data-tour="model-run-deps">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-page-text m-0">Where this model is used</h2>
          <button type="button" className={btnGhost + " text-xs"} onClick={loadDeps} disabled={deps.loading}>
            {deps.loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Refresh
          </button>
        </div>

        <div className="grid gap-2">
          {/* pipeline */}
          <DepRow icon={GitBranch} title="Pipeline transformations"
                  empty={!deps.loading && deps.strategies.length === 0}
                  emptyText="No ml_predict pipeline node references this model.">
            {deps.strategies.map((s) => (
              <a key={s.id} href={createPageUrl("pipeline")} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 text-sm text-nav-active no-underline hover:underline"
                 data-dep-strategy={s.id}>
                {s.label} <ExternalLink size={11} />
              </a>
            ))}
          </DepRow>

          {/* workflows */}
          <DepRow icon={Workflow} title="Workflow steps"
                  empty={!deps.loading && deps.workflows.length === 0}
                  emptyText="No workflow step calls cortex.ml.predict.">
            {deps.workflows.map((w) => (
              <a key={w.id} href={createPageUrl("workflows") + `?tab=processes&def=${encodeURIComponent(w.id)}`}
                 target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1 text-sm text-nav-active no-underline hover:underline"
                 data-dep-workflow={w.id}
                 title={w.mine ? "This process references this model" : "This process scores with cortex.ml.predict (model resolved at run time)"}>
                {w.name} · {w.steps} scoring step{w.steps === 1 ? "" : "s"}{w.mine ? "" : " (generic)"} <ExternalLink size={11} />
              </a>
            ))}
          </DepRow>

          {/* predictions in the governed target */}
          <DepRow icon={Database} title="Predictions written"
                  empty={!deps.loading && (!deps.predictions || deps.predictions.n === 0)}
                  emptyText="No rows in ml_prediction yet — batch-score from the training tab or a pipeline run.">
            {deps.predictions && deps.predictions.n > 0 && (
              <span className="text-sm text-page-text/80" data-dep-predictions={deps.predictions.n}>
                <b className="tabular-nums">{deps.predictions.n}</b> rows in{" "}
                <a href={createPageUrl("data-dictionary")} target="_blank" rel="noreferrer"
                   className="text-nav-active no-underline hover:underline">
                  <code className="font-mono">ml_prediction</code> <ExternalLink size={10} className="inline" />
                </a>
                {deps.predictions.latest != null && <> · latest from v{deps.predictions.latest}</>}
                {" "}— readable by apps, dashboards, the analyst, and workflow triggers.
              </span>
            )}
          </DepRow>

          {/* apps: honest, not empty-looking */}
          <DepRow icon={LayoutGrid} title="Apps" empty={false} emptyText="">
            <span className="text-sm text-page-text/60">
              Apps have no direct model binding — they consume this model&apos;s output through the{" "}
              <code className="font-mono">ml_prediction</code> table above.
            </span>
          </DepRow>
        </div>

        {deps.loading && (
          <div className="text-xs text-page-muted inline-flex items-center gap-1.5">
            <Loader2 size={12} className="animate-spin" /> Scanning pipeline and workflows…
          </div>
        )}
      </section>
    </div>
  );
}

function DepRow({ icon: Icon, title, empty, emptyText, children }) {
  return (
    <div className="rounded-md border border-card-border px-3 py-2 grid gap-1.5">
      <div className="flex items-center gap-1.5 text-xs font-medium text-page-text/70">
        <Icon size={13} className="opacity-70" />
        {title}
        <ChevronRight size={11} className="opacity-40" />
      </div>
      {empty
        ? <span className="text-sm text-page-text/50">{emptyText}</span>
        : <div className="flex flex-wrap gap-x-4 gap-y-1">{children}</div>}
    </div>
  );
}

// ── standalone page wrapper (models/:modelName/run) ─────────────────────────
export default function ModelRun() {
  const { modelName } = useParams();
  const { projectSlug } = useProject();
  const [state, setState] = useState({ loading: true, error: null, model: null, versions: [] });

  useEffect(() => {
    let gone = false;
    (async () => {
      try {
        const r = await listModels();
        const row = (r?.models || []).find((m) => m.name === modelName);
        if (!row) throw new Error(`No model named “${modelName}” in this project.`);
        const full = await getModel(row.id);
        if (!gone) setState({ loading: false, error: null, model: full.model, versions: full.versions || [] });
      } catch (e) {
        if (!gone) setState({ loading: false, error: String(e?.message || e), model: null, versions: [] });
      }
    })();
    return () => { gone = true; };
  }, [modelName, projectSlug]);

  if (state.loading) {
    return (
      <div className="h-full flex items-center justify-center text-page-muted gap-2 text-sm" data-tour="model-run-standalone">
        <Loader2 size={16} className="animate-spin" /> Loading model…
      </div>
    );
  }
  if (state.error || !state.model) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center px-6" data-tour="model-run-standalone">
        <AlertTriangle size={32} className="text-amber-500 mb-3" />
        <div className="text-page-text font-medium">Could not open the model runner</div>
        <div className="text-sm text-page-muted mt-1 max-w-md">{state.error}</div>
        <Link className={btnGhost + " mt-4 no-underline"} to={createPageUrl("models")}>
          <ArrowLeft size={14} /> Back to Models
        </Link>
      </div>
    );
  }

  const { model, versions } = state;
  const version = versions.find((v) => v.id === model.active_version_id) || null;
  return (
    <div className="h-full overflow-y-auto bg-page-bg" data-tour="model-run-standalone">
      <div className="max-w-[1100px] mx-auto p-5 grid gap-5">
        <div className="flex items-center gap-3 flex-wrap">
          <Link to={createPageUrl("models")} title="Back to Models"
                className="text-page-muted hover:text-page-text no-underline shrink-0">
            <ArrowLeft size={18} />
          </Link>
          <span className="text-xl" aria-hidden>{model.icon || "🧠"}</span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-base font-semibold text-page-text m-0 truncate">{model.display_name || model.name}</h1>
              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-nav-active/10 text-nav-active font-semibold">
                Test runner
              </span>
              {version && <span className="text-xs text-page-muted">active v{version.version}</span>}
            </div>
            <div className="text-xs text-page-muted mt-0.5">
              Predicts <code className="font-mono">{model.target_column || "—"}</code> · {model.task}
              {version?.dolt_commit && <> · trained from commit <code className="font-mono">{String(version.dolt_commit).slice(0, 10)}</code></>}
            </div>
          </div>
        </div>
        <ModelRunner model={model} versions={versions} />
      </div>
    </div>
  );
}
